import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  InputAdornment,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Autocomplete,
  Rating,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon,
  BrokenImage as DamageIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Save as SaveIcon,
  Receipt as DocumentIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  Remove as RemoveIcon,
  Replay as ReturnIcon,
  Warning as WarningIcon,
  Build as RepairIcon,
  DeleteForever as ScrapIcon,
  MonetizationOn as RefundIcon,
  SwapHoriz as ReplaceIcon,
  AttachMoney as MoneyIcon,
  Person as CustomerIcon,
  Business as VendorIcon,
} from "@mui/icons-material";

const DamagedReturn = () => {
  // Sample customers/vendors data
  const customers = [
    { id: 1, code: "CUST001", name: "Rajesh Kumar", type: "Customer" },
    { id: 2, code: "CUST002", name: "Priya Sharma", type: "Customer" },
    { id: 3, code: "CUST003", name: "Amit Patel", type: "Customer" },
    { id: 4, code: "VEN001", name: "ABC Suppliers Pvt Ltd", type: "Vendor" },
    { id: 5, code: "VEN002", name: "XYZ Wholesale", type: "Vendor" },
  ];

  // Sample products data
  const products = [
    { id: 1, code: "PRD001", name: "Samsung Galaxy S23", category: "Electronics", unit: "Piece", rate: 55000 },
    { id: 2, code: "PRD002", name: "Apple iPhone 14", category: "Electronics", unit: "Piece", rate: 75000 },
    { id: 3, code: "PRD003", name: "Sony LED TV 55\"", category: "Electronics", unit: "Piece", rate: 45000 },
    { id: 4, code: "PRD004", name: "HP Laptop i5", category: "Electronics", unit: "Piece", rate: 42000 },
    { id: 5, code: "PRD005", name: "Nike Sports Shoes", category: "Clothing", unit: "Pair", rate: 4500 },
    { id: 6, code: "PRD006", name: "Adidas T-Shirt", category: "Clothing", unit: "Piece", rate: 1200 },
    { id: 7, code: "PRD007", name: "LG Refrigerator 190L", category: "Home Appliances", unit: "Piece", rate: 18000 },
  ];

  // Damage/Return entries data
  const [damageReturnData, setDamageReturnData] = useState([
    {
      id: 1,
      entryNo: "DMG-2024-001",
      entryDate: "2024-01-15",
      entryType: "Damaged Stock",
      productName: "Samsung Galaxy S23",
      quantity: 2,
      damageReason: "Water damage during storage",
      severity: "High",
      estimatedLoss: 110000,
      actionTaken: "Scrap",
      relatedParty: "-",
      status: "Completed",
      createdDate: "2024-01-15",
    },
    {
      id: 2,
      entryNo: "RET-2024-001",
      entryDate: "2024-01-18",
      entryType: "Customer Return",
      productName: "Apple iPhone 14",
      quantity: 1,
      damageReason: "Screen defect - manufacturing fault",
      severity: "Medium",
      estimatedLoss: 0,
      actionTaken: "Replacement",
      relatedParty: "Rajesh Kumar",
      status: "Completed",
      createdDate: "2024-01-18",
    },
    {
      id: 3,
      entryNo: "RTV-2024-001",
      entryDate: "2024-01-20",
      entryType: "Return to Vendor",
      productName: "Sony LED TV 55\"",
      quantity: 3,
      damageReason: "Dead pixels - quality issue",
      severity: "High",
      estimatedLoss: 0,
      actionTaken: "Return to Vendor",
      relatedParty: "ABC Suppliers Pvt Ltd",
      status: "Pending",
      createdDate: "2024-01-20",
    },
    {
      id: 4,
      entryNo: "RET-2024-002",
      entryDate: "2024-01-22",
      entryType: "Customer Return",
      productName: "Nike Sports Shoes",
      quantity: 1,
      damageReason: "Wrong size - customer request",
      severity: "Low",
      estimatedLoss: 0,
      actionTaken: "Refund",
      relatedParty: "Priya Sharma",
      status: "Completed",
      createdDate: "2024-01-22",
    },
    {
      id: 5,
      entryNo: "DMG-2024-002",
      entryDate: "2024-01-25",
      entryType: "Damaged Stock",
      productName: "HP Laptop i5",
      quantity: 1,
      damageReason: "Accidental drop - physical damage",
      severity: "High",
      estimatedLoss: 42000,
      actionTaken: "Repair",
      relatedParty: "-",
      status: "In Progress",
      createdDate: "2024-01-25",
    },
  ]);

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [actionFilter, setActionFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [successMessage, setSuccessMessage] = useState("");
  const [errors, setErrors] = useState({});

  // Modal state
  const [openModal, setOpenModal] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [viewingEntry, setViewingEntry] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Entry form state
  const [entryForm, setEntryForm] = useState({
    entryNo: "",
    entryDate: new Date().toISOString().split('T')[0],
    entryType: "Damaged Stock",
    relatedPartyId: null,
    invoiceRef: "",
    remarks: "",
    status: "Pending",
  });

  // Entry items state
  const [entryItems, setEntryItems] = useState([
    {
      id: Date.now(),
      productId: null,
      productName: "",
      quantity: 1,
      rate: 0,
      totalValue: 0,
      damageReason: "",
      severity: "Medium",
      actionTaken: "Pending Review",
      conditionRating: 0,
    }
  ]);

  // Helper functions
  const getFilteredEntries = () => {
    return damageReturnData.filter((entry) => {
      const matchesSearch = Object.values(entry).some((value) =>
        String(value).toLowerCase().includes(searchQuery.toLowerCase())
      );
      const matchesStatus = statusFilter === "all" || entry.status === statusFilter;
      const matchesType = typeFilter === "all" || entry.entryType === typeFilter;
      const matchesAction = actionFilter === "all" || entry.actionTaken === actionFilter;
      return matchesSearch && matchesStatus && matchesType && matchesAction;
    });
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Completed':
        return { backgroundColor: '#e8f5e9', color: '#2e7d32' };
      case 'Pending':
        return { backgroundColor: '#fff3e0', color: '#e65100' };
      case 'In Progress':
        return { backgroundColor: '#e3f2fd', color: '#1976d2' };
      case 'Cancelled':
        return { backgroundColor: '#ffebee', color: '#d32f2f' };
      default:
        return { backgroundColor: '#f5f5f5', color: '#666' };
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'Damaged Stock': return 'error';
      case 'Customer Return': return 'warning';
      case 'Return to Vendor': return 'info';
      case 'Warranty Claim': return 'secondary';
      default: return 'default';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'High': return 'error';
      case 'Medium': return 'warning';
      case 'Low': return 'success';
      default: return 'default';
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'Scrap': return <ScrapIcon />;
      case 'Repair': return <RepairIcon />;
      case 'Refund': return <RefundIcon />;
      case 'Replacement': return <ReplaceIcon />;
      case 'Return to Vendor': return <ReturnIcon />;
      default: return <WarningIcon />;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Calculate totals
  const calculateTotals = () => {
    let totalQuantity = 0;
    let totalValue = 0;
    let totalItems = 0;

    entryItems.forEach(item => {
      if (item.productId) {
        totalQuantity += item.quantity;
        totalValue += item.totalValue;
        totalItems++;
      }
    });

    return { totalQuantity, totalValue, totalItems };
  };

  // Handlers
  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setOpenModal(true);
  };

  const handleView = (entry) => {
    setViewingEntry(entry);
    setOpenViewModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this entry?")) {
      setDamageReturnData(damageReturnData.filter((entry) => entry.id !== id));
      setSuccessMessage("Entry deleted successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    }
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = () => {
    setPage(0);
    setSuccessMessage(`Search completed. Found ${getFilteredEntries().length} entries.`);
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const handleReset = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setTypeFilter("all");
    setActionFilter("all");
    setPage(0);
    setErrors({});
    setSuccessMessage("");
  };

  const handleOpenModal = () => {
    setEditingEntry(null);
    const newEntryNo = `${entryForm.entryType === "Customer Return" || entryForm.entryType === "Return to Vendor" ? "RET" : "DMG"}-${new Date().getFullYear()}-${String(damageReturnData.length + 1).padStart(3, '0')}`;
    setEntryForm({
      entryNo: newEntryNo,
      entryDate: new Date().toISOString().split('T')[0],
      entryType: "Damaged Stock",
      relatedPartyId: null,
      invoiceRef: "",
      remarks: "",
      status: "Pending",
    });
    setEntryItems([
      {
        id: Date.now(),
        productId: null,
        productName: "",
        quantity: 1,
        rate: 0,
        totalValue: 0,
        damageReason: "",
        severity: "Medium",
        actionTaken: "Pending Review",
        conditionRating: 0,
      }
    ]);
    setErrors({});
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingEntry(null);
    setErrors({});
  };

  const handleCloseViewModal = () => {
    setOpenViewModal(false);
    setViewingEntry(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setEntryForm({ ...entryForm, [name]: value });
    
    // Auto-generate entry number based on type
    if (name === "entryType") {
      let prefix = "";
      if (value === "Customer Return" || value === "Return to Vendor" || value === "Warranty Claim") {
        prefix = "RET";
      } else {
        prefix = "DMG";
      }
      const entryNo = `${prefix}-${new Date().getFullYear()}-${String(damageReturnData.length + 1).padStart(3, '0')}`;
      setEntryForm(prev => ({ ...prev, entryNo }));
    }
  };

  const handleRelatedPartyChange = (event, newValue) => {
    setEntryForm({ 
      ...entryForm, 
      relatedPartyId: newValue ? newValue.id : null 
    });
  };

  const handleProductChange = (index, event, newValue) => {
    const updatedItems = [...entryItems];
    if (newValue) {
      updatedItems[index] = {
        ...updatedItems[index],
        productId: newValue.id,
        productName: newValue.name,
        rate: newValue.rate,
        totalValue: updatedItems[index].quantity * newValue.rate,
      };
    } else {
      updatedItems[index] = {
        ...updatedItems[index],
        productId: null,
        productName: "",
        rate: 0,
        totalValue: 0,
      };
    }
    setEntryItems(updatedItems);
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...entryItems];
    
    if (field === 'quantity') {
      updatedItems[index][field] = parseInt(value) || 0;
      updatedItems[index].totalValue = updatedItems[index].quantity * updatedItems[index].rate;
    } else if (field === 'conditionRating') {
      updatedItems[index][field] = parseFloat(value) || 0;
    } else {
      updatedItems[index][field] = value;
    }
    
    setEntryItems(updatedItems);
  };

  const handleAddItem = () => {
    setEntryItems([
      ...entryItems,
      {
        id: Date.now(),
        productId: null,
        productName: "",
        quantity: 1,
        rate: 0,
        totalValue: 0,
        damageReason: "",
        severity: "Medium",
        actionTaken: "Pending Review",
        conditionRating: 0,
      }
    ]);
  };

  const handleRemoveItem = (index) => {
    if (entryItems.length > 1) {
      const updatedItems = entryItems.filter((_, i) => i !== index);
      setEntryItems(updatedItems);
    }
  };

  const handleSaveEntry = async (isDraft = false) => {
    // Validation
    if (entryForm.entryType !== "Damaged Stock" && !entryForm.relatedPartyId) {
      setErrors({ relatedParty: "Please select customer/vendor for return entries" });
      return;
    }
    const validItems = entryItems.filter(item => item.productId);
    if (validItems.length === 0) {
      setErrors({ items: "Please add at least one product" });
      return;
    }

    setIsLoading(true);
    try {
      const totals = calculateTotals();
      let relatedParty = "-";
      if (entryForm.relatedPartyId) {
        const party = customers.find(c => c.id === entryForm.relatedPartyId);
        relatedParty = party.name;
      }

      // Get first item for summary (in real app, would handle multiple items better)
      const firstItem = validItems[0];

      const newEntry = {
        id: damageReturnData.length + 1,
        entryNo: entryForm.entryNo,
        entryDate: entryForm.entryDate,
        entryType: entryForm.entryType,
        productName: firstItem.productName,
        quantity: totals.totalQuantity,
        damageReason: firstItem.damageReason,
        severity: firstItem.severity,
        estimatedLoss: totals.totalValue,
        actionTaken: firstItem.actionTaken,
        relatedParty: relatedParty,
        status: isDraft ? "Pending" : entryForm.status,
        createdDate: new Date().toISOString().split('T')[0],
      };

      setDamageReturnData([newEntry, ...damageReturnData]);
      setSuccessMessage(`Entry ${isDraft ? 'saved as pending' : 'created'} successfully!`);
      handleCloseModal();
    } catch (error) {
      setErrors({ general: "An error occurred. Please try again." });
    } finally {
      setIsLoading(false);
      setTimeout(() => setSuccessMessage(""), 3000);
    }
  };

  const filteredEntries = getFilteredEntries();
  const totals = calculateTotals();

  // Get unique values for filters
  const entryTypes = [...new Set(damageReturnData.map(e => e.entryType))];
  const actions = [...new Set(damageReturnData.map(e => e.actionTaken))];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <DamageIcon sx={{ color: 'error.main', fontSize: 40 }} />
          <Box>
            <Typography variant="h4" fontWeight="bold" color="primary">
              Damage & Return Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Track damaged stock, customer returns, and vendor returns • {filteredEntries.length} entries found
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenModal}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            fontWeight: 600,
            backgroundColor: 'primary.main',
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
          }}
        >
          New Entry
        </Button>
      </Box>

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Search and Filter Section */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Search Entries..."
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              select
              fullWidth
              label="Type"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              SelectProps={{
                native: true,
              }}
            >
              <option value="all">All Types</option>
              {entryTypes.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              select
              fullWidth
              label="Action"
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              SelectProps={{
                native: true,
              }}
            >
              <option value="all">All Actions</option>
              {actions.map((action) => (
                <option key={action} value={action}>{action}</option>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              select
              fullWidth
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              SelectProps={{
                native: true,
              }}
            >
              <option value="all">All Status</option>
              <option value="Pending">Pending</option>
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleSearch}
                startIcon={<SearchIcon />}
                sx={{ flex: 1 }}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                onClick={handleReset}
                startIcon={<RefreshIcon />}
                sx={{ flex: 1 }}
              >
                Reset
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Table Section */}
      <Paper sx={{ borderRadius: 2, boxShadow: 3, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>S.No</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Entry No</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Product/Qty</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Reason</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Severity</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Loss Value</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Action</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEntries.length > 0 ? (
                filteredEntries
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((entry, index) => (
                    <TableRow 
                      key={entry.id} 
                      sx={{ 
                        '&:hover': { backgroundColor: '#f5f5f5' },
                        transition: 'background-color 0.2s'
                      }}
                    >
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="600" sx={{ fontFamily: 'monospace' }}>
                          {entry.entryNo}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {entry.entryDate}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={entry.entryType} 
                          size="small" 
                          color={getTypeColor(entry.entryType)}
                          sx={{ fontWeight: 500 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="600">
                            {entry.productName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Qty: {entry.quantity}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ display: 'block', maxWidth: 200 }}>
                          {entry.damageReason}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={entry.severity} 
                          size="small" 
                          color={getSeverityColor(entry.severity)}
                          sx={{ fontWeight: 500 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="700" color={entry.estimatedLoss > 0 ? "error.main" : "text.secondary"}>
                          {entry.estimatedLoss > 0 ? formatCurrency(entry.estimatedLoss) : "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {getActionIcon(entry.actionTaken)}
                          <Typography variant="caption" fontWeight="600">
                            {entry.actionTaken}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={entry.status}
                          size="small"
                          sx={{
                            ...getStatusStyle(entry.status),
                            fontWeight: 600,
                            fontSize: '0.75rem'
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleView(entry)}
                              sx={{
                                color: 'info.main',
                                '&:hover': { backgroundColor: 'info.light' },
                              }}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(entry)}
                              sx={{
                                color: 'warning.main',
                                '&:hover': { backgroundColor: 'warning.light' },
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(entry.id)}
                              sx={{
                                color: 'error.main',
                                '&:hover': { backgroundColor: 'error.light' },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
              ) : (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                    <DamageIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">
                      No entries found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredEntries.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Entry Modal */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="xl" fullWidth>
        <DialogTitle sx={{
          backgroundColor: 'primary.main',
          color: 'white',
          padding: '16px 24px',
        }}>
          <Typography variant="h6" component="div" sx={{ color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
            <DamageIcon />
            {editingEntry ? 'Edit Entry' : 'New Damage/Return Entry'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ padding: '24px', mt: 2 }}>
          {/* Entry Header Information */}
          <Paper sx={{ p: 3, mb: 3, backgroundColor: '#f8f9fa' }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">
              Entry Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Entry No"
                  name="entryNo"
                  value={entryForm.entryNo}
                  disabled
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <DocumentIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Entry Date"
                  name="entryDate"
                  type="date"
                  value={entryForm.entryDate}
                  onChange={handleFormChange}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Entry Type *</InputLabel>
                  <Select
                    name="entryType"
                    value={entryForm.entryType}
                    onChange={handleFormChange}
                    label="Entry Type *"
                  >
                    <MenuItem value="Damaged Stock">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DamageIcon /> Damaged Stock
                      </Box>
                    </MenuItem>
                    <MenuItem value="Customer Return">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ReturnIcon /> Customer Return
                      </Box>
                    </MenuItem>
                    <MenuItem value="Return to Vendor">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <VendorIcon /> Return to Vendor
                      </Box>
                    </MenuItem>
                    <MenuItem value="Warranty Claim">Warranty Claim</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              {entryForm.entryType !== "Damaged Stock" && (
                <>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      options={customers.filter(c => 
                        (entryForm.entryType === "Customer Return" && c.type === "Customer") ||
                        (entryForm.entryType === "Return to Vendor" && c.type === "Vendor") ||
                        entryForm.entryType === "Warranty Claim"
                      )}
                      getOptionLabel={(option) => `${option.code} - ${option.name}`}
                      onChange={handleRelatedPartyChange}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={entryForm.entryType === "Customer Return" ? "Customer *" : "Vendor *"}
                          error={!!errors.relatedParty}
                          helperText={errors.relatedParty}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  {entryForm.entryType === "Customer Return" ? <CustomerIcon /> : <VendorIcon />}
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Invoice/Order Reference"
                      name="invoiceRef"
                      value={entryForm.invoiceRef}
                      onChange={handleFormChange}
                      placeholder="Original invoice/order number"
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Paper>

          {/* Product Items Table */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight="bold" color="primary">
                Product Details
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddItem}
                size="small"
              >
                Add Item
              </Button>
            </Box>
            {errors.items && (
              <Alert severity="error" sx={{ mb: 2 }}>{errors.items}</Alert>
            )}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 'bold', width: '3%' }}>S.No</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '20%' }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '8%' }}>Qty</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '10%' }}>Value</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '20%' }}>Reason</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '10%' }}>Severity</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '10%' }}>Condition</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>Action</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '4%' }}>Del</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entryItems.map((item, index) => (
                    <TableRow key={item.id}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <Autocomplete
                          options={products}
                          getOptionLabel={(option) => `${option.code} - ${option.name}`}
                          onChange={(event, newValue) => handleProductChange(index, event, newValue)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Select Product"
                              size="small"
                              fullWidth
                            />
                          )}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          size="small"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                          inputProps={{ min: 1, step: 1 }}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" fontWeight="bold">
                          {formatCurrency(item.totalValue)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={item.damageReason}
                          onChange={(e) => handleItemChange(index, 'damageReason', e.target.value)}
                          placeholder="Describe issue..."
                          fullWidth
                          multiline
                        />
                      </TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          value={item.severity}
                          onChange={(e) => handleItemChange(index, 'severity', e.target.value)}
                          fullWidth
                        >
                          <MenuItem value="Low">Low</MenuItem>
                          <MenuItem value="Medium">Medium</MenuItem>
                          <MenuItem value="High">High</MenuItem>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Rating
                          value={item.conditionRating}
                          onChange={(e, newValue) => handleItemChange(index, 'conditionRating', newValue)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          value={item.actionTaken}
                          onChange={(e) => handleItemChange(index, 'actionTaken', e.target.value)}
                          fullWidth
                        >
                          <MenuItem value="Pending Review">Pending Review</MenuItem>
                          <MenuItem value="Scrap">Scrap</MenuItem>
                          <MenuItem value="Repair">Repair</MenuItem>
                          <MenuItem value="Refund">Refund</MenuItem>
                          <MenuItem value="Replacement">Replacement</MenuItem>
                          <MenuItem value="Return to Vendor">Return to Vendor</MenuItem>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveItem(index)}
                          disabled={entryItems.length === 1}
                          color="error"
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Remarks and Summary */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">
                  Additional Information
                </Typography>
                <TextField
                  fullWidth
                  label="Remarks / Notes"
                  name="remarks"
                  value={entryForm.remarks}
                  onChange={handleFormChange}
                  multiline
                  rows={6}
                  placeholder="Additional notes, corrective actions, etc..."
                />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, backgroundColor: '#fff3f3', height: '100%' }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom color="error">
                  Summary
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body1">Total Items:</Typography>
                    <Typography variant="body1" fontWeight="600">
                      {totals.totalItems}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body1">Total Quantity:</Typography>
                    <Typography variant="body1" fontWeight="600">
                      {totals.totalQuantity} units
                    </Typography>
                  </Box>
                  <Divider />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Typography variant="h6" fontWeight="bold">Estimated Loss:</Typography>
                    <Typography variant="h6" fontWeight="bold" color="error.main">
                      {formatCurrency(totals.totalValue)}
                    </Typography>
                  </Box>
                  <Alert severity="warning" icon={<WarningIcon />}>
                    This entry will affect inventory levels and may impact financial records.
                  </Alert>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {errors.general && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errors.general}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px', gap: 1 }}>
          <Button onClick={handleCloseModal} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={() => handleSaveEntry(true)}
            disabled={isLoading}
            startIcon={<SaveIcon />}
          >
            Save as Pending
          </Button>
          <Button
            variant="contained"
            onClick={() => handleSaveEntry(false)}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
            sx={{
              backgroundColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
            }}
          >
            {isLoading ? 'Processing...' : 'Create Entry'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Entry Modal */}
      <Dialog open={openViewModal} onClose={handleCloseViewModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{
          backgroundColor: 'primary.main',
          color: 'white',
          padding: '16px 24px',
        }}>
          <Typography variant="h6" component="div" sx={{ color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon />
            Entry Details
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ padding: '24px', mt: 2 }}>
          {viewingEntry && (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Entry No</Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ mt: 1, fontFamily: 'monospace' }}>
                    {viewingEntry.entryNo}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Entry Date</Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ mt: 1 }}>
                    {viewingEntry.entryDate}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Entry Type</Typography>
                  <Box sx={{ mt: 1 }}>
                    <Chip 
                      label={viewingEntry.entryType} 
                      color={getTypeColor(viewingEntry.entryType)}
                    />
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Related Party</Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ mt: 1 }}>
                    {viewingEntry.relatedParty}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Product</Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ mt: 1 }}>
                    {viewingEntry.productName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Quantity: {viewingEntry.quantity}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Severity</Typography>
                  <Box sx={{ mt: 1 }}>
                    <Chip 
                      label={viewingEntry.severity} 
                      color={getSeverityColor(viewingEntry.severity)}
                    />
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Damage/Return Reason</Typography>
                  <Typography variant="body1" sx={{ mt: 1 }}>
                    {viewingEntry.damageReason}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(244, 67, 54, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Estimated Loss</Typography>
                  <Typography variant="h6" fontWeight="bold" sx={{ mt: 1 }} color="error.main">
                    {viewingEntry.estimatedLoss > 0 ? formatCurrency(viewingEntry.estimatedLoss) : "No Loss"}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Action Taken</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    {getActionIcon(viewingEntry.actionTaken)}
                    <Typography variant="body1" fontWeight="bold">
                      {viewingEntry.actionTaken}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(144, 19, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      label={viewingEntry.status}
                      sx={{
                        ...getStatusStyle(viewingEntry.status),
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button onClick={handleCloseViewModal}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DamagedReturn;

